/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * What the Equipe tab shows before it has a team to count.
 *
 * The caption used to render a null team as zeros. These two states stand
 * in that gap: a skeleton while the request is out, and a retry when it
 * failed before any answer arrived.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataStateError } from '@/shared/ui';
import { SKELETON_BLOCK, skeletonSlots } from '@/shared/ui/components/basic/data-state/skeletons/skeleton-primitives';

const PLACEHOLDER_CARDS = 3;

/**
 * The loading state: a sentence the reader can hear, and cards where the
 * list will be.
 *
 * @returns A busy region that does not count anybody.
 */
export function TeamLoading(): React.ReactElement {
  const { t } = useTranslation('features/agency-settings');
  return (
    <div
      data-testid="team-loading"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('v2.team.loading')}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-small)',
          lineHeight: 1.5,
          color: 'var(--text-tertiary-soft)',
        }}
      >
        {t('v2.team.loading')}
      </p>
      <div
        aria-hidden="true"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        {skeletonSlots(PLACEHOLDER_CARDS).map((slot) => (
          <div
            key={slot}
            data-testid="team-loading-card"
            className={SKELETON_BLOCK}
            style={{
              height: 80,
              borderRadius: 'var(--radius-xl)',
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'var(--border-subtle)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export interface TeamLoadErrorProps {
  /** Reads the team again. */
  onRetry: () => void;
}

/**
 * The first read failed. There is still no team, so this is not an empty one.
 *
 * @param props - The retry.
 * @returns The failure, with a way to ask again.
 */
export function TeamLoadError({ onRetry }: TeamLoadErrorProps): React.ReactElement {
  const { t } = useTranslation('features/agency-settings');
  return (
    <div
      role="alert"
      data-testid="team-load-error"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-8)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--surface-primary)',
      }}
    >
      <DataStateError
        message={t('v2.team.loadError')}
        onRetry={onRetry}
        retryLabel={t('v2.team.retry')}
      />
    </div>
  );
}
